import type { Cache } from '@urql/exchange-graphcache';
import { graphql, type ResultOf } from '@/gql/graphql';

const SummaryFragment = graphql(`
  fragment OptimisticRsvpTeamSummary on event_team_rsvp_summary {
    __typename
    event_id
    team_id
    yes
    maybe
    no
  }
`);

type Status = ReturnType<typeof graphql.scalar<'rsvp_status_enum'>> | null;

export default function rsvpTeamSummaries(
  cache: Pick<
    Cache,
    'inspectFields' | 'resolve' | 'readFragment' | 'writeFragment'
  >,
  eventId: string,
  personId: string,
  status: Status,
) {
  const summaries: ResultOf<typeof SummaryFragment>[] = [];
  const previousStatus = cache.resolve(
    { __typename: 'person', id: personId },
    'rsvp',
    { args: { event_id: eventId } },
  );
  if (
    previousStatus !== null &&
    previousStatus !== 'yes' &&
    previousStatus !== 'maybe' &&
    previousStatus !== 'no'
  ) {
    return summaries;
  }
  const eventKey = `event:${eventId}`;
  const seenTeams = new Set<string>();
  for (const field of cache.inspectFields(eventKey)) {
    if (field.fieldName !== 'event_teams') continue;
    const teams = cache.resolve(eventKey, field.fieldKey);
    if (!Array.isArray(teams)) continue;
    for (const teamLink of teams) {
      if (typeof teamLink !== 'string') continue;
      const teamId = cache.resolve(teamLink, 'team_id');
      if (typeof teamId !== 'string' || seenTeams.has(teamId)) continue;
      seenTeams.add(teamId);
      const memberType = cache.resolve(
        { __typename: 'person_team', person_id: personId, team_id: teamId },
        'member_type',
      );
      if (memberType !== 'participant') continue;
      const summary = cache.readFragment(SummaryFragment, {
        __typename: 'event_team_rsvp_summary',
        event_id: eventId,
        team_id: teamId,
      });
      if (!summary) continue;
      const next = { ...summary };
      if (previousStatus !== null)
        next[previousStatus] = Math.max(0, (next[previousStatus] ?? 0) - 1);
      if (status !== null) next[status] = (next[status] ?? 0) + 1;
      cache.writeFragment(SummaryFragment, next);
      summaries.push(next);
    }
  }
  return summaries;
}
